import { useCallback, useEffect, useMemo } from 'react'

import { useLazyGetVacationHoldListQuery } from 'api/accounts/vacationHoldsCrud'
import { HOLD_TYPES } from 'settings/constants/service'
import { isVacationHoldActiveWithinRange } from 'utils/vacation'

import type { VacationHoldApi } from 'types/api/account/vacationHold'

const groupByLocation = (holds: VacationHoldApi[]) => {
  const byLocation = new Map<string, VacationHoldApi[]>()
  holds.forEach(hold =>
    (hold.locations ?? []).forEach(locationId => byLocation.set(locationId, [...(byLocation.get(locationId) ?? []), hold]))
  )
  return byLocation
}

/** Loads an account's holds and answers, per location, how many are active and which vacation holds are running today. */
const useLocationVacationHolds = (accountId?: string | null) => {
  const [getVacationHoldList, { data, isLoading, isFetching }] = useLazyGetVacationHoldListQuery()

  useEffect(() => {
    if (accountId) getVacationHoldList({ accountId, deleted: false })
  }, [accountId, getVacationHoldList])

  const activeVacationHolds = useMemo(() => (data?.vacationHolds ?? []).filter(hold => hold.active), [data])
  const runningVacationHolds = useMemo(
    () =>
      activeVacationHolds.filter(
        ({ type, startDate, endDate }) => type === HOLD_TYPES.VACATION && startDate && isVacationHoldActiveWithinRange(startDate, endDate)
      ),
    [activeVacationHolds]
  )
  const activeByLocation = useMemo(() => groupByLocation(activeVacationHolds), [activeVacationHolds])
  const runningByLocation = useMemo(() => groupByLocation(runningVacationHolds), [runningVacationHolds])

  const getVacationHoldCountForLocation = useCallback(
    (locationId?: string | null) => (locationId ? activeByLocation.get(locationId)?.length ?? 0 : 0),
    [activeByLocation]
  )
  const hasVacationHolds = useCallback(
    (locationId?: string | null) => getVacationHoldCountForLocation(locationId) > 0,
    [getVacationHoldCountForLocation]
  )
  const getRunningVacationHolds = useCallback((locationId: string) => runningByLocation.get(locationId) ?? [], [runningByLocation])

  return {
    activeVacationHolds,
    runningVacationHolds,
    getVacationHoldCountForLocation,
    hasVacationHolds,
    getRunningVacationHolds,
    isLoading: isLoading || isFetching,
  }
}

export default useLocationVacationHolds
